import React, { Component, createRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import TextFieldComponent from '../TextField/TextFieldComponent';
import ButtonGroupComponent from '../ButtonGroup/ButtonGroupComponent';
import DropdownComponent from '../DropDown/DropDownComponent';
import CardButtonGroupComponent from '../CardButtonGroup/CardButtonGroupComponent';
import PillDateSelectComponent from '../PillDateSelect/PillDateSelectComponent';
import SegmentedScaleComponent from '../SegmentedScale/SegmentedScaleComponent';
import AddressBarComponent from '../AddressBar/AddressBarComponent';
import AddressWithMap from '../AddressWithMap/AddressWithMapComponent';
import AutoCompleteComponent from '../AutoComplete/AutoCompleteComponent';
import VehicleSearchComponent from '../VehicleSearch/VehicleSearchComponent';
import CheckBoxComponent from '../CheckBox/CheckBoxComponent';
import SearchTagSelectComponent from '../SearchTagSelect/SearchTagSelectComponent';
import DateRangePickerComponent from '../DateRangePicker/DateRangePickerComponent';
import PetDetailsComponent from '../PetDetails/PetDetailsComponent';
import IndustryCategoryComponent from '../IndustryCategory/IndustryCategoryComponent';
import AsyncDropdownWithExtraOption from '../AsyncDropdownWithExtraOption/AsyncDropdownWithExtraOptionComponent';
import ComboCategorySelectorComponent from '../ComboCategorySelector/ComboCategorySelectorComponent';
import { getBrand } from '../../utils/carBrand';
import gsap from 'gsap';
import LoadingDots from '../LoadingDots/LoadingDots';
import PopupModalComponent from '../PopupModal/PopupModalComponent';
import { toSentenceCase } from '../../utils/casing';
import { isValidatedWithin } from '../../utils/questionEngineOtpHelpers';

// lottie-web + assets
import lottie from 'lottie-web';
import smHearts from '../../assets/lottie/sm-hearts.json';
import smExclamation from '../../assets/lottie/sm-exclamation.json';
import smThumbsUp from '../../assets/lottie/sm-thumbs-up.json';
import MotorBikeSearch from '../VehicleSearch/MotorBikeSearchComponent';

class QuestionEngineComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      validationStates: {},
      showConfettiDisclaimer: false,
      isLoadingDots: false,

      // runtime celebration data (per selection)
      currentCelebrationDisclaimer: null, // {title, text}
      showLottie: false,

      // modal state
      isModalOpen: false,
    };

    this.validationTriggers = {};
    this.lastSelections = {};

    // Refs
    this.confettiRef = React.createRef();
    this.staggerRef = createRef();

    // Lottie
    this.lottieContainerRef = createRef();
    this.lottieInstance = null;

    // CTA button ref (for bounce)
    this.buttonLabelRef = null;
  }

  /* ------------------------------------
   * Generic helpers
   * ------------------------------------ */

  getFromPath = (obj, path) => {
    if (!path) return undefined;
    return String(path)
      .split('.')
      .reduce(
        (acc, p) => (acc && acc[p] !== undefined ? acc[p] : undefined),
        obj
      );
  };

  setByPathImmutable = (rootObj, path, value) => {
    if (!path) return rootObj;
    const parts = String(path).split('.');
    const rootKey = parts.shift();
    if (!rootKey) return rootObj;

    const newRoot = Array.isArray(rootObj) ? [...rootObj] : { ...rootObj };

    if (parts.length === 0) {
      newRoot[rootKey] = value;
      return newRoot;
    }

    let cursor = newRoot[rootKey] ?? {};
    cursor = Array.isArray(cursor) ? [...cursor] : { ...cursor };

    let obj = cursor;
    for (let i = 0; i < parts.length - 1; i++) {
      const k = parts[i];
      const next = obj[k];
      obj[k] = Array.isArray(next)
        ? [...next]
        : typeof next === 'object' && next !== null
          ? { ...next }
          : {};
      obj = obj[k];
    }

    obj[parts[parts.length - 1]] = value;
    newRoot[rootKey] = cursor;
    return newRoot;
  };

  clearPaths = (paths = [], clearTo = '') => {
    if (!Array.isArray(paths) || !paths.length) return;
    const { values, onChange } = this.props;

    let nextValues = values;
    const changedRoots = new Set();

    for (const p of paths) {
      if (!p || typeof p !== 'string') continue;
      nextValues = this.setByPathImmutable(nextValues, p, clearTo);
      changedRoots.add(String(p).split('.')[0]);
    }

    for (const rootKey of changedRoots) {
      onChange(rootKey, nextValues[rootKey]);
    }
  };

  // extraOption clearing before moving NEXT
  runPreNextExtraOptionClears = () => {
    const { prop, values } = this.props;
    if (!prop?.questions?.length) return;

    for (const group of prop.questions) {
      for (const [key, qc] of Object.entries(group)) {
        if (key === 'layout' || key === 'columns') continue;

        if (
          !qc?.extraOptionLabel ||
          !Array.isArray(qc?.clearFieldsOnExtraOption)
        ) {
          continue;
        }

        const fieldName = qc?.name || key;
        const currentVal = this.getFromPath(values, fieldName);

        if (String(currentVal) === String(qc.extraOptionLabel)) {
          this.clearPaths(qc.clearFieldsOnExtraOption, '');
        }
      }
    }
  };

  getPossessive = (gender) => {
    if (!gender) return '';
    const lower = String(gender).toLowerCase();
    if (lower === 'male') return 'his';
    if (lower === 'female') return 'her';
    return 'their';
  };

  substituteTemplateVars = (template) => {
    const { values } = this.props;

    const substituted = template.replace(
      /\$([a-zA-Z0-9_.]+)/g,
      (match, path) => {
        const val = this.getFromPath(values, path);

        if (val === undefined || val === null || String(val).trim() === '') {
          return '';
        }

        return String(val);
      }
    );

    return substituted;
  };

  /* ------------------------------------
   * Modal handlers
   * ------------------------------------ */

  handleModalOpen = () => {
    this.setState({ isModalOpen: true });
  };

  handleModalClose = () => {
    this.setState({ isModalOpen: false });
  };

  handleModalAction = (payload) => {
    if (this.props.onLearnMoreAction) {
      this.props.onLearnMoreAction(payload);
    }
    this.setState({ isModalOpen: false });
  };

  /* ------------------------------------
   * Lifecycle + animations
   * ------------------------------------ */

  componentDidMount() {
    this.prepStaggerElements();
    setTimeout(() => this.runStaggerInAnimation(), 30);
  }

  // Drop the previous step's validators before the new step's fields register
  // theirs. This has to happen here rather than in componentDidUpdate: React
  // runs children's mount/update lifecycles before the parent's
  // componentDidUpdate, so clearing there wiped the validators that had just
  // registered — leaving the Next button with nothing to run, and no error
  // under an empty field.
  getSnapshotBeforeUpdate(prevProps) {
    if (prevProps.prop !== this.props.prop) {
      this.validationTriggers = {};
    }
    return null;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.prop !== this.props.prop) {
      const freshState = {};
      this.props.prop?.questions?.forEach((group) => {
        Object.entries(group).forEach(([key, qc]) => {
          if (key === 'layout' || key === 'columns') return;
          const fieldName = qc?.name || key;
          freshState[fieldName] = true;
        });
      });

      this.setState(
        {
          validationStates: freshState,
          showConfettiDisclaimer: false,
          currentCelebrationDisclaimer: null,
          showLottie: false,
        },
        () => {
          this.prepStaggerElements();
          setTimeout(() => this.runStaggerInAnimation(), 30);
        }
      );

      this.destroyLottie();
    }
  }

  componentWillUnmount() {
    this.destroyLottie();
  }

  prepStaggerElements = () => {
    const el = this.staggerRef?.current;
    if (!el) return;

    const elements = el.querySelectorAll('.stagger-in');
    if (elements.length > 0) {
      gsap.set(elements, { opacity: 0, y: 30 });
    }
  };

  runStaggerInAnimation = () => {
    const el = this.staggerRef?.current;
    if (!el) return;

    const elements = el.querySelectorAll('.stagger-in');
    if (elements.length === 0) return;

    gsap.set(elements, { opacity: 0, y: 30 });
    gsap.to(elements, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: 'power2.out',
      stagger: 0.1,
      overwrite: true,
    });
  };

  fadeOutCurrent = () => {
    const el = this.staggerRef?.current;
    if (!el) return Promise.resolve();

    const elements = el.querySelectorAll('.stagger-in');
    return new Promise((resolve) => {
      gsap.to(elements, {
        opacity: 0,
        y: 20,
        duration: 0.9,
        ease: 'power2.inOut',
        onComplete: resolve,
      });
    });
  };

  /* ------------------------------------
   * Validation wiring
   * ------------------------------------ */

  setValidationState = (key, isValid) => {
    this.setState((prevState) => ({
      validationStates: { ...prevState.validationStates, [key]: isValid },
    }));
  };

  registerValidation = (key, triggerFn) => {
    this.validationTriggers[key] = triggerFn;
  };

  runAllValidationTriggers = () => {
    // One bad validator must not take the whole step down with it: this runs
    // inside the click handler, so a throw here rejects handlePrimaryAdvance
    // and the button silently does nothing — no advance, and none of the other
    // fields get to show their errors. Treat a thrower as invalid instead.
    return Object.values(this.validationTriggers).map((trigger) => {
      try {
        return trigger();
      } catch {
        return false;
      }
    });
  };

  validateGroup(group) {
    let allOk = true;

    for (const [key, qc] of Object.entries(group)) {
      if (key === 'layout' || key === 'columns') continue;

      const fieldName = qc?.name || key;
      const value = this.getFromPath(this.props.values, fieldName);

      // Special: license issue date as YYYY/MM string
      if (
        fieldName === 'mainDriverDetails.licenseFirstIssueDate' &&
        qc?.type === 'text-field'
      ) {
        const s =
          this.getFromPath(
            this.props.values,
            'mainDriverDetails.licenseFirstIssueDateStr'
          ) || '';
        const re = qc?.validation?.pattern
          ? new RegExp(qc.validation.pattern)
          : /^(\d{4})\/(0[1-9]|1[0-2])$/;

        const ok = !qc?.validation?.required
          ? !s || re.test(String(s).trim())
          : re.test(String(s).trim());

        if (!ok) {
          this.validationTriggers?.[fieldName]?.();
          this.setValidationState(fieldName, false);
          allOk = false;
          continue;
        }
        this.setValidationState(fieldName, true);
        continue;
      }

      if (!qc?.validation?.required) {
        // Not required → does not block
        continue;
      }

      let ok = false;

      if (qc.type === 'vehiclesearch') {
        const desc =
          typeof value === 'string'
            ? value
            : value?.SearchDescription || value?.VehicleDescription || '';
        ok = String(desc).trim().length > 0;
      } else if (qc.type === 'motorbikesearch') {
        const desc =
          typeof value === 'string'
            ? value
            : value?.MotorcycleDescription || value?.SearchDescription || '';
        ok = String(desc).trim().length > 0;
      } else if (typeof value === 'string') {
        ok = value.trim().length > 0;
      } else if (Array.isArray(value)) {
        ok = value.length > 0;
      } else if (value && typeof value === 'object') {
        if (qc.type === 'pill-date-select') {
          const parts = qc.options?.map((o) => o.value) || [
            'year',
            'month',
            'day',
          ];
          ok = parts.every((p) => value?.[p] && value[p] !== '');
        } else {
          ok = Object.values(value).every((v) => v && v !== '');
        }
      } else {
        ok = value !== undefined && value !== null;
      }

      if (!ok) {
        this.validationTriggers?.[fieldName]?.();
        this.setValidationState(fieldName, false);
        allOk = false;
      } else {
        this.setValidationState(fieldName, true);
      }
    }

    return allOk;
  }

  /* ------------------------------------
   * Celebration helpers (Lottie + confetti)
   * ------------------------------------ */

  getCelebrationForSelection = (question, selected) => {
    const list = Array.isArray(question?.celebration)
      ? question.celebration
      : null;
    if (!list) return null;

    const s = String(selected);
    return (
      list.find(
        (c) =>
          Array.isArray(c?.onOptions) && c.onOptions.map(String).includes(s)
      ) || null
    );
  };

  getLottieForType = (type) => {
    const key = String(type || '').toLowerCase();
    if (key.includes('heart')) return smHearts;
    if (key.includes('exclamation')) return smExclamation;
    if (key.includes('thumb')) return smThumbsUp;
    return smHearts;
  };

  destroyLottie = () => {
    if (this.lottieInstance) {
      this.lottieInstance.destroy();
      this.lottieInstance = null;
    }
  };

  playLottieCelebration = (type) => {
    const animationData = this.getLottieForType(type);
    this.setState({ showLottie: true }, () => {
      setTimeout(() => {
        this.destroyLottie();
        if (!this.lottieContainerRef.current) return;

        this.lottieInstance = lottie.loadAnimation({
          container: this.lottieContainerRef.current,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          animationData,
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid meet',
            progressiveLoad: true,
          },
        });

        this.lottieInstance.addEventListener('complete', () => {
          this.setState({ showLottie: false }, () => {
            this.destroyLottie();
          });
        });
      }, 0);
    });
  };

  /* ------------------------------------
   * Navigation helpers
   * ------------------------------------ */

  handleNext = () => {
    const { onNext } = this.props;
    onNext();
  };

  // Runs the same validate + animate + advance sequence as the primary
  // ("Looks Good") button. Reused so an option like "+ Add Another Vehicle"
  // can advance on selection without a second click on the button.
  handlePrimaryAdvance = async () => {
    const { isLoading } = this.props;
    if (isLoading || this.state.isLoadingDots) return;

    this.runPreNextExtraOptionClears();

    const el = this.buttonLabelRef;

    // A trigger's return value is the only synchronous verdict available here:
    // `validationStates` is written via setState and may not have flushed yet,
    // and `validateGroup` only covers fields the config marks required. A
    // trigger that returns nothing is treated as a pass, as before.
    const settledTriggers = await Promise.allSettled(
      this.runAllValidationTriggers()
    );
    const triggersValid = settledTriggers.every(
      (result) => result.status === 'fulfilled' && result.value !== false
    );

    const isValidGroup = this.props.prop.questions.every((group) =>
      this.validateGroup(group)
    );
    const allValid = Object.values(this.state.validationStates).every(
      (v) => v !== false
    );
    if (!triggersValid || !isValidGroup || !allValid) return;

    this.setState({ isLoadingDots: true });

    const elements =
      this.staggerRef?.current?.querySelectorAll('.stagger-in') || [];

    const tl = gsap.timeline({ defaults: { overwrite: 'auto' } });

    if (el) {
      tl.to(el, {
        scale: 0.85,
        duration: 0.15,
        ease: 'power3.inOut',
      }).to(
        el,
        {
          scale: 1,
          duration: 0.5,
          ease: 'elastic.out(1.75, 0.4)',
        },
        '>'
      );
    }

    if (elements.length) {
      tl.to(
        elements,
        {
          opacity: 0,
          y: 20,
          duration: 0.9,
          ease: 'power2.inOut',
        },
        '>-0.03'
      );
    }

    await new Promise((resolve) => tl.eventCallback('onComplete', resolve));

    this.setState({ isLoadingDots: false });
    this.handleNext();
  };

  /* ------------------------------------
   * Render helpers
   * ------------------------------------ */

  renderTitleLines = (title) => {
    const { values } = this.props;

    const substituted = title.replace(/\$([a-zA-Z0-9_.]+)/g, (match, path) => {
      if (path === 'meetingGreeting') {
        return isValidatedWithin()
          ? 'Great to see you again,'
          : 'Nice to meet you,';
      }

      if (path === 'carDetails.SearchDescription') {
        const rawCar = this.getFromPath(values, path);
        return rawCar ? getBrand(rawCar) : match;
      }

      if (['Gender', 'additionalDriverGender'].includes(path)) {
        const gender = this.getFromPath(values, path);
        return this.getPossessive(gender);
      }

      if (path.endsWith('GenderPossessive')) {
        const basePath = path.replace(/Possessive$/, '');
        const gender = this.getFromPath(values, basePath);
        return this.getPossessive(gender);
      }

      const value = this.getFromPath(values, path);
      return value !== undefined ? value : match;
    });

    // Note: kept original "/n" split for compatibility
    return substituted.split('/n').map((line, index) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <span key={index}>
          {parts.map((part, i) =>
            part.startsWith('**') && part.endsWith('**') ? (
              <strong key={i}>{part.slice(2, -2)}</strong>
            ) : (
              <React.Fragment key={i}>{part}</React.Fragment>
            )
          )}
          <br />
        </span>
      );
    });
  };

  renderConfettiDisclaimer = (confettiDisclaimer) => {
    const { isMobile } = this.props;
    const disclaimer = confettiDisclaimer;

    if (!disclaimer || (!disclaimer.title && !disclaimer.text)) return null;

    return (
      <Box
        ref={this.confettiRef}
        className="stagger-in"
        sx={{
          width: '100%',
          maxWidth: '498px',
          px: isMobile ? 2 : 0,
          mt: '2rem',
          mb: isMobile ? 3 : 2,
          textAlign: 'center',
          zIndex: 1,
          position: 'relative',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            lineHeight: '120%',
            color: '#B3B3B3',
          }}
        >
          {disclaimer.title && (
            <>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  color: '#1C2F3A',
                  display: 'inline-block',
                  marginBottom: '8px',
                }}
              >
                {disclaimer.title}
              </span>
              <br />
              <br />
            </>
          )}
          {String(disclaimer.text || '')
            .split('\n')
            .map((line, i) => (
              <span key={i}>
                {line}
                <br />
              </span>
            ))}
        </Typography>

        {this.state.showLottie && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: isMobile ? '-70px' : '-100px',
              width: isMobile ? 280 : 350,
              height: isMobile ? 280 : 350,
              pointerEvents: 'none',
            }}
            ref={this.lottieContainerRef}
          />
        )}
      </Box>
    );
  };

  renderDisclaimer = (disclaimer) => {
    const { isMobile } = this.props;

    if (!disclaimer || (!disclaimer.title && !disclaimer.text)) return null;

    return (
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          hangingPunctuation: true,
          textAlign: 'center',
          color: '#A2ACB1',
          mt: '2rem',
          ...(isMobile && {
            maxWidth: '100%',
            mx: 'auto',
            width: '80vw',
          }),
        }}
      >
        {disclaimer.title && (
          <>
            <span
              style={{
                fontWeight: 700,
                fontSize: '0.875rem',
                color: '#162F3B',
                display: 'inline-block',
                height: '20px',
              }}
            >
              {disclaimer.title}
            </span>
            <br />
          </>
        )}
        {String(disclaimer.text || '')
          .split('\n')
          .map((line, i) => (
            <span key={i}>
              {line}
              <br />
            </span>
          ))}
        <br />
      </Typography>
    );
  };

  renderDisclaimerSection = (prop, activeCelebrationDisclaimer) => {
    if (this.state.showConfettiDisclaimer) {
      return this.renderConfettiDisclaimer(activeCelebrationDisclaimer);
    }

    return (
      <Box
        className="stagger-in"
        sx={{
          zIndex: 1,
          position: 'relative',
          maxWidth: '448px',
          width: '448px',
          textAlign: 'center',
          mt: !prop.disclaimer?.title && !prop.disclaimer?.text ? '24px' : 0,
        }}
      >
        {this.renderDisclaimer(prop.disclaimer)}
      </Box>
    );
  };

  renderPrimaryButton = () => {
    const { prop, isLoading } = this.props;
    if (!prop.buttonLabel || this.state.showConfettiDisclaimer) return null;

    // ✅ Special-case ONLY AddressWithMap steps
    const isAddressWithMapStep = prop?.questions?.some((group) =>
      Object.values(group).some(
        (q) => q?.type === 'address-bar' && q?.mapPreview?.enabled
      )
    );

    const fibreOk =
      this.props.values?.fibreAvailability?.wasSuccessful === true &&
      this.props.values?.fibreAvailability?.hasFibre === true;

    if (isAddressWithMapStep && !fibreOk) return null;

    return (
      <Box
        className="stagger-in"
        sx={{
          zIndex: 1,
          position: 'relative',
          width: '100%',
          maxWidth: '498px',
          display: 'flex',
          justifyContent: 'center',
          mb: '10px',
          mx: 'auto',
          mt: prop.borderBox?.borderBox ? '24px' : '56px',
        }}
      >
        <Button
          ref={(el) => (this.buttonLabelRef = el)}
          disabled={isLoading || this.state.isLoadingDots}
          onClick={this.handlePrimaryAdvance}
          sx={{
            backgroundColor: '#C7CA00',
            color: '#162F3B',
            borderRadius: '20px',
            padding: '12px 24px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '1.25rem',
            textTransform: 'none',
            '&.Mui-disabled': {
              backgroundColor: '#D7D9DB',
              color: '#8A949B',
            },
            '&:hover': { backgroundColor: '#B0B300' },
          }}
        >
          {this.state.isLoadingDots ? (
            <LoadingDots />
          ) : (
            toSentenceCase(this.props.prop.buttonLabel)
          )}
        </Button>
      </Box>
    );
  };

  renderLearnMore = (learnMore) => {
    if (!learnMore) return null;

    return (
      <Typography
        className="stagger-in"
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.875rem',
          fontWeight: 600,
          lineHeight: '1.25rem',
          color: '#A2ACB1',
          textAlign: 'center',
          cursor: 'pointer',
          px: '30px',
          py: '15px',
          textDecoration: 'none',
          borderRadius: '8px',
          mx: 'auto',
          '&:hover': { color: '#555' },
        }}
        onClick={(e) => {
          e.preventDefault();
          this.handleModalOpen();
        }}
      >
        Learn more
      </Typography>
    );
  };

  renderBackButton = () => {
    if (!this.props.canGoBack) return null;

    return (
      <Typography
        className="stagger-in"
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.875rem',
          fontWeight: 600,
          lineHeight: '1.25rem',
          color: '#A2ACB1',
          textAlign: 'center',
          cursor: 'pointer',
          width: '99px',
          height: '22px',
          px: '30px',
          py: '15px',
          textDecoration: 'none',
          borderRadius: '8px',
          mx: 'auto',
          '&:hover': {
            color: '#555',
          },
        }}
        component="a"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          this.props.onBack();
        }}
      >
        Back
      </Typography>
    );
  };

  /* ------------------------------------
   * Question rendering
   * ------------------------------------ */

  renderQuestion = (
    key,
    question,
    onNext,
    totalFields,
    fieldIndex,
    fieldRefs
  ) => {
    const { values, onChange } = this.props;

    const ref = fieldRefs?.[fieldIndex] || null;
    const nextRef = fieldRefs?.[fieldIndex + 1] || null;

    const autoAdvanceTypes = [
      'button-group',
      'card-button-group',
      'segmented-scale',
      'dropdown',
      'pill-date-select',
      'address-bar',
      'autocomplete',
      'textfield',
    ];

    const shouldAutoAdvance =
      !this.props.prop.buttonLabel &&
      autoAdvanceTypes.includes(question.type) &&
      (question.autoAdvance === true || totalFields === 1);

    const fieldName = question.name || key;
    const currentValue = this.getFromPath(values, fieldName);

    const fmtLfi = (obj) =>
      obj?.year && obj?.month
        ? `${String(obj.year).padStart(4, '0')}/${String(obj.month).padStart(
            2,
            '0'
          )}`
        : '';

    const parseLfi = (s) => {
      if (!s) return null;
      const m = String(s)
        .trim()
        .match(/^(\d{4})[/-](0[1-9]|1[0-2])$/);
      return m ? { year: m[1], month: m[2], day: '' } : null;
    };

    const isLicenseIssuedField =
      question.type === 'text-field' &&
      fieldName === 'mainDriverDetails.licenseFirstIssueDate';

    const displayValue = isLicenseIssuedField
      ? (this.getFromPath(
          values,
          'mainDriverDetails.licenseFirstIssueDateStr'
        ) ??
        fmtLfi(
          this.getFromPath(values, 'mainDriverDetails.licenseFirstIssueDate')
        ))
      : (currentValue ?? '');

    if (isLicenseIssuedField) {
      const pattern = question.validation?.pattern
        ? new RegExp(question.validation.pattern)
        : /^(\d{4})[/-](0[1-9]|1[0-2])$/;

      this.registerValidation(fieldName, () => {
        const s =
          this.getFromPath(
            values,
            'mainDriverDetails.licenseFirstIssueDateStr'
          ) ??
          fmtLfi(
            this.getFromPath(values, 'mainDriverDetails.licenseFirstIssueDate')
          ) ??
          '';

        const ok = !!s && pattern.test(String(s).trim());
        this.setValidationState(fieldName, ok);
        return ok;
      });
    }

    const componentProps = {
      name: fieldName,
      question: { ...question },
      value: displayValue,
      values,
      onChange: (path, newValue, ...rest) => {
        this.lastSelections[path] = newValue;

        if (isLicenseIssuedField) {
          const rootKey = 'mainDriverDetails';
          const nextRoot = {
            ...(values[rootKey] || {}),
            licenseFirstIssueDateStr: newValue,
          };

          const parsed = parseLfi(newValue);
          nextRoot.licenseFirstIssueDate = parsed || {
            year: '',
            month: '',
            day: '',
          };

          this.props.onChange(rootKey, nextRoot, ...rest);

          const pattern = question.validation?.pattern
            ? new RegExp(question.validation.pattern)
            : /^(\d{4})[/-](0[1-9]|1[0-2])$/;
          const ok = !!newValue && pattern.test(String(newValue).trim());
          this.setValidationState(fieldName, ok);

          const match = this.getCelebrationForSelection(question, newValue);
          if (match) {
            this.setState(
              {
                showConfettiDisclaimer: true,
                currentCelebrationDisclaimer: match.confettiDisclaimer || null,
              },
              () =>
                setTimeout(() => this.playLottieCelebration(match.type), 100)
            );
          }
          return;
        }

        const nextValuesForRoot = this.setByPathImmutable(
          this.props.values,
          path,
          newValue
        );
        const rootKey = String(path).split('.')[0];

        onChange(rootKey, nextValuesForRoot[rootKey], ...rest);

        const match = this.getCelebrationForSelection(question, newValue);
        if (match) {
          this.setState(
            {
              showConfettiDisclaimer: true,
              currentCelebrationDisclaimer: match.confettiDisclaimer || null,
            },
            () => setTimeout(() => this.playLottieCelebration(match.type), 100)
          );
        }
      },
      onNext:
        !this.props.prop.buttonLabel &&
        autoAdvanceTypes.includes(question.type) &&
        shouldAutoAdvance
          ? async (selectedOverride) => {
              this.runPreNextExtraOptionClears();

              const selected =
                selectedOverride ??
                this.lastSelections[fieldName] ??
                this.getFromPath(this.props.values, fieldName);

              const match = this.getCelebrationForSelection(question, selected);
              if (match) {
                this.setState(
                  {
                    showConfettiDisclaimer: true,
                    currentCelebrationDisclaimer:
                      match.confettiDisclaimer || null,
                  },
                  () =>
                    setTimeout(
                      () => this.playLottieCelebration(match.type),
                      100
                    )
                );

                await new Promise((r) => setTimeout(r, 2000));
                await this.fadeOutCurrent();

                const branching = question.branching?.[selected];
                if (
                  branching?.product &&
                  branching?.questionIndex !== undefined
                ) {
                  this.props.switchFlow(
                    branching.product,
                    branching.questionIndex
                  );
                } else {
                  this.handleNext();
                }
                return;
              }

              await this.fadeOutCurrent();
              setTimeout(() => {
                const branching = question.branching?.[selected];
                if (
                  branching?.product &&
                  branching?.questionIndex !== undefined
                ) {
                  this.props.switchFlow(
                    branching.product,
                    branching.questionIndex
                  );
                } else {
                  this.handleNext();
                }
              }, 0);
            }
          : undefined,
      onExtraOptionAdvance: this.handlePrimaryAdvance,
      totalFields,
      setValidationState: this.setValidationState,
      registerValidation: this.registerValidation,
      prop: this.props.prop,
      inputRef: ref,
      nextFieldRef: nextRef,
      isMobile: this.props.isMobile,
    };

    switch (question.type) {
      case 'text-field':
        return <TextFieldComponent key={key} {...componentProps} />;
      case 'button-group':
        return <ButtonGroupComponent key={key} {...componentProps} />;
      case 'dropdown':
        return <DropdownComponent key={key} {...componentProps} />;
      case 'card-button-group':
        return <CardButtonGroupComponent key={key} {...componentProps} />;
      case 'pill-date-select': {
        let licenseIssueDOB = null;
        let minLicenseAge = null;

        if (
          fieldName === 'licenseFirstIssue' ||
          fieldName === 'additionalDriverLicenseFirstIssue'
        ) {
          const licenseType = String(
            fieldName === 'additionalDriverLicenseFirstIssue'
              ? values?.additionalDriverDriverLicense
              : values?.mainDriverLicense || ''
          ).toLowerCase();

          if (licenseType.includes('learner')) {
            minLicenseAge = 17;
          } else if (licenseType.includes('international')) {
            minLicenseAge = 16;
          } else if (
            licenseType.includes('driver') ||
            licenseType.includes('sa')
          ) {
            minLicenseAge = 18;
          }

          if (fieldName === 'additionalDriverLicenseFirstIssue') {
            licenseIssueDOB = values?.additionalDriverBirthday || null;
          } else if (values?.birthday) {
            licenseIssueDOB = values.birthday;
          } else if (values?.idNumber && String(values.idNumber).length >= 6) {
            const id = String(values.idNumber);
            const yy = id.substring(0, 2);
            const mm = id.substring(2, 4);
            const dd = id.substring(4, 6);
            const currentYY = new Date().getFullYear() % 100;
            const century = parseInt(yy, 10) <= currentYY ? '20' : '19';
            licenseIssueDOB = {
              year: `${century}${yy}`,
              month: mm,
              day: dd,
            };
          }
        }
        return (
          <PillDateSelectComponent
            key={key}
            {...componentProps}
            dob={licenseIssueDOB}
            minLicenseAge={minLicenseAge}
          />
        );
      }
      case 'segmented-scale':
        return <SegmentedScaleComponent key={key} {...componentProps} />;
      case 'address-bar':
        if (question?.mapPreview?.enabled) {
          return <AddressWithMap key={key} {...componentProps} />;
        }
        return <AddressBarComponent key={key} {...componentProps} />;
      case 'autocomplete':
        return <AutoCompleteComponent key={key} {...componentProps} />;
      case 'async-dropdown-extra':
        return <AsyncDropdownWithExtraOption key={key} {...componentProps} />;
      case 'checkbox':
        return <CheckBoxComponent key={key} {...componentProps} />;
      case 'combo-category-select':
        return <ComboCategorySelectorComponent key={key} {...componentProps} />;
      case 'search-tag-select':
        return <SearchTagSelectComponent key={key} {...componentProps} />;
      case 'date-range':
        return <DateRangePickerComponent key={key} {...componentProps} />;
      case 'vehiclesearch':
        return (
          <VehicleSearchComponent
            key={key}
            {...componentProps}
            vehicleYear={this.props.values?.yearOfManufacturer}
          />
        );
      case 'motorbikesearch':
        return (
          <MotorBikeSearch
            key={key}
            {...componentProps}
            vehicleYear={this.props.values?.yearOfMotorCycleManufacturer}
          />
        );
      case 'industryCategorySearch':
        return <IndustryCategoryComponent key={key} {...componentProps} />;
      case 'petdetails':
        return (
          <PetDetailsComponent
            key={fieldName}
            {...componentProps}
            field={question}
            onNext={this.handleNext}
          />
        );
      default:
        return null;
    }
  };

  renderQuestionGroups = () => {
    const { prop, isMobile } = this.props;

    return (
      <Box sx={{ width: '100%' }}>
        {prop.questions.map((questionGroup, groupIndex) => {
          const entries = Object.entries(questionGroup).filter(
            ([key]) => key !== 'layout' && key !== 'columns'
          );
          const sorted = entries.sort(
            ([, a], [, b]) => a.orderNumber - b.orderNumber
          );
          const totalFields = sorted.length;
          const fieldRefs = sorted.map(() => React.createRef());

          const effectiveColumnCount = isMobile
            ? 1
            : questionGroup.columns ||
              (questionGroup.layout === 'column' ? 1 : 2);
          const isSingleColumn = effectiveColumnCount === 1;

          return (
            <Box
              key={groupIndex}
              className="stagger-in"
              sx={{
                display: 'flex',
                flexWrap: isSingleColumn ? 'nowrap' : 'wrap',
                flexDirection: isSingleColumn ? 'column' : 'row',
                justifyContent: 'center',
                alignItems: isSingleColumn ? 'center' : 'flex-start',
                columnGap: isSingleColumn ? '15px' : '15px',
                rowGap: isSingleColumn ? '26px' : '30px',
                width: '100%',
                overflow: 'visible',
                maxWidth: '100%',
                margin: '0 auto',
              }}
            >
              {sorted.map(([key, question], index) => (
                <Box
                  key={key}
                  className="stagger-in"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: isSingleColumn ? 'auto' : '240px',
                    overflow: 'visible',
                  }}
                >
                  {this.renderQuestion(
                    key,
                    question,
                    this.props.onNext,
                    totalFields,
                    index,
                    fieldRefs
                  )}
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    );
  };

  /* ------------------------------------
   * Main render
   * ------------------------------------ */

  render() {
    const { isModalOpen } = this.state;
    const { prop, isMobile, isLoading } = this.props;

    if (!prop || !prop.questions || prop.questions.length === 0) return null;

    const isBorderOn = !!prop.borderBox?.borderBox;
    const HippoAvatar =
      'https://assets.askhippo.co.za/avatars/v2/Thumbs-up-v2.gif';
    const PeekABoo =
      'https://assets.askhippo.co.za/avatars/v2/Peak-a-boo-v2.gif';
    // pick which disclaimer to render: per-selection (state) or none

    const activeCelebrationDisclaimer = this.state.showConfettiDisclaimer
      ? this.state.currentCelebrationDisclaimer
      : null;

    return (
      <Box
        ref={this.staggerRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '100%',
          overflow: 'visible',
          boxSizing: 'border-box',
          minHeight: '100vh',
          pt: isMobile ? '0.5rem' : '2rem',
          paddingBottom: '2rem',
          position: isMobile ? 'relative' : 'static',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '448px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            px: isMobile ? 0 : undefined,
          }}
        >
          <Box
            component="img"
            alt="avatar"
            src={this.props.usePeekABooAvatar ? PeekABoo : HippoAvatar}
            sx={{
              width: '124px',
              height: '124px',
              mb: '1rem',
              position: 'relative',
              objectFit: 'contain',
            }}
          />

          {isLoading && <LoadingDots variant="otp" />}

          {prop.title && (
            <Typography
              className="stagger-in"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: isMobile ? '1.25rem' : '1.5rem',
                lineHeight: isMobile ? '1.5rem' : '1.75rem',
                letterSpacing: '-0.03em',
                verticalAlign: 'middle',
                textAlign: 'center',
                maxWidth: '100%',
                width: isMobile ? '80vw' : '100%',
                position: 'relative',
                mb: isMobile ? '2rem' : '3.6rem',
                whiteSpace: 'pre-line',
              }}
            >
              {this.renderTitleLines(prop.title)}
            </Typography>
          )}
        </Box>

        {/* Bordered question box */}
        <Box
          className="stagger-in"
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: '739px',
            zIndex: 1300,
            overflow: 'visible',
            border: isBorderOn ? '1px solid #E3E6E8' : 'none',
            borderRadius: isBorderOn ? '16px' : '0',
            backgroundColor: isBorderOn ? '#fff' : 'transparent',
            pb: isBorderOn ? '3rem' : 0,
            mb: isBorderOn ? '2.8rem' : 0,
          }}
        >
          {prop.borderBox?.title && (
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                color: '#162F3B',
                textAlign: 'center',
                mb: '31px',
                mt: '33px',
              }}
            >
              {this.substituteTemplateVars(prop.borderBox.title)}
            </Typography>
          )}

          {this.renderQuestionGroups()}

          {/* If border is ON: inside box → primary button + back */}
          {isBorderOn && (
            <>
              {this.renderPrimaryButton()}
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {this.renderBackButton()}
              </Box>
            </>
          )}
        </Box>

        {/* Border OFF: bottom stack → CTA → disclaimer → learn more/back */}
        {!isBorderOn && (
          <>
            {this.renderPrimaryButton()}
            {this.renderDisclaimerSection(prop, activeCelebrationDisclaimer)}
            {prop.learnMore
              ? this.renderLearnMore(prop.learnMore)
              : this.renderBackButton()}
          </>
        )}

        {/* Modal */}
        {prop.learnMore && (
          <PopupModalComponent
            data={prop.learnMore}
            open={isModalOpen}
            onClose={this.handleModalClose}
            onAction={this.handleModalAction}
            isMobile={isMobile}
          />
        )}

        {/* Border ON: disclaimer/Lottie is always outside, after the box */}
        {isBorderOn &&
          this.renderDisclaimerSection(prop, activeCelebrationDisclaimer)}
      </Box>
    );
  }
}

export default QuestionEngineComponent;
